(() => {
  if (customElements.get('venja-pack-selector')) return;

  class VenjaPackSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.tabs = Array.from(this.querySelectorAll('[data-pack-tab]'));
      this.cards = Array.from(this.querySelectorAll('[data-pack-card]'));
      this.panels = Array.from(this.querySelectorAll('[data-pack-panel]'));
      this.cardLinks = this.cards.map((card) => Array.from(card.querySelectorAll('[data-pack-card-link]')));
      this.tabRow = this.querySelector('[data-pack-tabs]');
      this.rail = this.querySelector('[data-pack-rail]');
      this.currentNumber = this.querySelector('[data-pack-current]');
      this.progressValue = this.querySelector('[data-pack-progress]');
      this.desktopNavigation = this.querySelector('[data-pack-desktop-navigation]');
      this.desktopRangeStart = this.querySelector('[data-pack-range-start]');
      this.desktopRangeEnd = this.querySelector('[data-pack-range-end]');
      this.desktopRangeTotal = this.querySelector('[data-pack-range-total]');
      this.previousButton = this.querySelector('[data-pack-previous]');
      this.nextButton = this.querySelector('[data-pack-next]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopLayout = window.matchMedia('(min-width: 990px)');

      if (!this.tabs.length || !this.cards.length || !this.rail) return;

      this.activeIndex = 0;
      this.desktopStartIndex = 0;
      this.visibleDesktopCards = 3;
      this.programmaticScroll = false;
      this.scrollFrame = null;
      this.programmaticTimer = null;
      this.initialized = true;

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onRailKeydown = this.onRailKeydown.bind(this);
      this.onRailScroll = this.onRailScroll.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.onDesktopPrevious = this.onDesktopPrevious.bind(this);
      this.onDesktopNext = this.onDesktopNext.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      this.rail.addEventListener('keydown', this.onRailKeydown);
      this.rail.addEventListener('scroll', this.onRailScroll, { passive: true });
      this.desktopLayout.addEventListener('change', this.onLayoutChange);
      if (this.previousButton) this.previousButton.addEventListener('click', this.onDesktopPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onDesktopNext);
      this.update(0);
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });

      this.rail.removeEventListener('keydown', this.onRailKeydown);
      this.rail.removeEventListener('scroll', this.onRailScroll);
      this.desktopLayout.removeEventListener('change', this.onLayoutChange);
      if (this.previousButton) this.previousButton.removeEventListener('click', this.onDesktopPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onDesktopNext);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.programmaticTimer);
      this.initialized = false;
    }

    onTabClick(event) {
      const index = Number(event.currentTarget.dataset.packIndex);
      this.select(index, { moveCard: true });
    }

    onTabKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();
      let nextIndex = this.activeIndex;

      if (event.key === 'ArrowRight') nextIndex = (this.activeIndex + 1) % this.cards.length;
      if (event.key === 'ArrowLeft') nextIndex = (this.activeIndex - 1 + this.cards.length) % this.cards.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.cards.length - 1;

      this.select(nextIndex, { moveCard: true, focusTab: true });
    }

    onRailKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = Math.min(Math.max(this.activeIndex + direction, 0), this.cards.length - 1);
      this.select(nextIndex, { moveCard: true });
    }

    onRailScroll() {
      if (this.programmaticScroll) return;
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const railRect = this.rail.getBoundingClientRect();
        const referencePoint = this.desktopLayout.matches
          ? railRect.left
          : railRect.left + railRect.width / 2;
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const cardPoint = this.desktopLayout.matches
            ? cardRect.left
            : cardRect.left + cardRect.width / 2;
          const distance = Math.abs(cardPoint - referencePoint);

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        if (this.desktopLayout.matches) {
          this.desktopStartIndex = Math.min(nearestIndex, this.getDesktopMaxStart());
          nearestIndex = this.desktopStartIndex;
        }

        if (nearestIndex !== this.activeIndex) {
          this.update(nearestIndex);
          this.centerTab(nearestIndex);
        } else {
          this.updateDesktopNavigation();
        }
      });
    }

    onLayoutChange() {
      if (this.desktopLayout.matches) {
        this.desktopStartIndex = Math.min(this.activeIndex, this.getDesktopMaxStart());
      }
      this.update(this.activeIndex);
    }

    onDesktopPrevious() {
      this.moveDesktopRange(this.desktopStartIndex - 1);
    }

    onDesktopNext() {
      this.moveDesktopRange(this.desktopStartIndex + 1);
    }

    select(index, options = {}) {
      const safeIndex = Math.min(Math.max(index, 0), this.cards.length - 1);

      if (options.moveCard && this.desktopLayout.matches) {
        this.desktopStartIndex = Math.min(safeIndex, this.getDesktopMaxStart());
        this.update(safeIndex);
        this.centerTab(safeIndex);
        this.scrollDesktopToStart(this.desktopStartIndex);
        if (options.focusTab) this.tabs[safeIndex].focus();
        return;
      }

      this.update(safeIndex);
      this.centerTab(safeIndex);

      if (options.moveCard) this.centerCard(safeIndex);
      if (options.focusTab) this.tabs[safeIndex].focus();
    }

    update(index) {
      this.activeIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.cards.forEach((card, cardIndex) => {
        const active = cardIndex === index;
        card.dataset.active = String(active);
        this.cardLinks[cardIndex].forEach((link) => {
          link.tabIndex = active || this.desktopLayout.matches ? 0 : -1;
        });
      });

      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });

      if (this.currentNumber) {
        this.currentNumber.textContent = String(index + 1).padStart(2, '0');
      }

      if (this.progressValue) {
        this.progressValue.style.width = `${((index + 1) / this.cards.length) * 100}%`;
      }

      this.updateDesktopNavigation();
    }

    getDesktopMaxStart() {
      return Math.max(0, this.cards.length - this.visibleDesktopCards);
    }

    moveDesktopRange(index) {
      const safeStart = Math.min(Math.max(index, 0), this.getDesktopMaxStart());
      if (safeStart === this.desktopStartIndex) return;

      this.desktopStartIndex = safeStart;
      this.update(safeStart);
      this.centerTab(safeStart);
      this.scrollDesktopToStart(safeStart);
    }

    updateDesktopNavigation() {
      if (!this.desktopNavigation) return;

      const maxStart = this.getDesktopMaxStart();
      this.desktopStartIndex = Math.min(Math.max(this.desktopStartIndex, 0), maxStart);
      const visibleEnd = Math.min(
        this.desktopStartIndex + this.visibleDesktopCards,
        this.cards.length,
      );

      if (this.desktopRangeStart) this.desktopRangeStart.textContent = String(this.desktopStartIndex + 1);
      if (this.desktopRangeEnd) this.desktopRangeEnd.textContent = String(visibleEnd);
      if (this.desktopRangeTotal) this.desktopRangeTotal.textContent = String(this.cards.length);
      if (this.previousButton) this.previousButton.disabled = this.desktopStartIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.desktopStartIndex === maxStart;
    }

    scrollDesktopToStart(index) {
      const card = this.cards[index];
      if (!card) return;

      const railRect = this.rail.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const left = this.rail.scrollLeft + cardRect.left - railRect.left;

      this.programmaticScroll = true;
      this.rail.scrollTo({
        left: Math.max(0, left),
        behavior: 'auto',
      });
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = window.setTimeout(() => {
        this.programmaticScroll = false;
      }, 50);
    }

    centerCard(index) {
      const card = this.cards[index];
      if (!card) return;

      this.programmaticScroll = true;
      this.centerWithin(this.rail, card, 'auto');
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = window.setTimeout(() => {
        this.programmaticScroll = false;
      }, 50);
    }

    centerTab(index) {
      const tab = this.tabs[index];
      if (!tab || !this.tabRow) return;
      this.centerWithin(this.tabRow, tab);
    }

    centerWithin(container, item, behavior = 'smooth') {
      const containerRect = container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const left = container.scrollLeft
        + itemRect.left
        - containerRect.left
        - (containerRect.width - itemRect.width) / 2;

      container.scrollTo({
        left: Math.max(0, left),
        behavior: this.reduceMotion.matches ? 'auto' : behavior,
      });
    }
  }

  customElements.define('venja-pack-selector', VenjaPackSelector);
})();
